import { useMemo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronRight, Plus } from "lucide-react";
import { Badge, Button, Skeleton, Text } from "@musa.tech/design-system";
import { cn } from "@/lib/utils";
import { ColetaCalendarCard } from "./ColetaCalendarCard";
import {
  dayKey,
  getWeekDays,
  startOfToday,
} from "@/utils/coletasCalendar";
import type { DiaCalendario } from "@/hooks/useProximasColetas";
import type { CollectForTable } from "@/hooks/useCollectsApiData";

interface SemanaViewProps {
  referenceDate: Date;
  byDay: Map<string, DiaCalendario>;
  isLoading: boolean;
  onSelect?: (collect: CollectForTable) => void;
  /** Abre o drawer com todas as coletas do dia (clique no cabeçalho ou "Ver mais"). */
  onOpenDay?: (dayKey: string) => void;
}

export const SemanaView = ({
  referenceDate,
  byDay,
  isLoading,
  onSelect,
  onOpenDay,
}: SemanaViewProps) => {
  const days = useMemo(() => getWeekDays(referenceDate), [referenceDate]);
  const todayKey = dayKey(startOfToday());

  if (isLoading) {
    return <Skeleton className="h-96 w-full rounded-lg" />;
  }

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[700px] grid-cols-7 gap-2">
        {days.map((day) => {
          const key = dayKey(day);
          const entry = byDay.get(key);
          const collects = entry?.collects ?? [];
          const total = entry?.total ?? 0;
          const overflow = total - collects.length;
          const isToday = key === todayKey;
          const hasCollects = total > 0;
          return (
            <div
              key={key}
              className={cn(
                "flex flex-col overflow-hidden rounded-lg border bg-white transition-shadow",
                isToday
                  ? "border-primary-300 ring-1 ring-primary-200"
                  : "border-neutral-200",
              )}
            >
              {/* Cabeçalho clicável — abre todas as coletas do dia */}
              <button
                type="button"
                onClick={() => onOpenDay?.(key)}
                disabled={!hasCollects}
                aria-label={`Ver todas as coletas de ${format(day, "dd 'de' MMMM", { locale: ptBR })}`}
                className={cn(
                  "group flex items-center justify-between gap-1 border-b px-2.5 py-2 text-left transition-colors",
                  isToday
                    ? "border-primary-200 bg-primary-50"
                    : "border-neutral-100 bg-neutral-50",
                  hasCollects && "cursor-pointer hover:bg-primary-100",
                )}
              >
                <div className="min-w-0">
                  <Text
                    variant="label"
                    size="small"
                    className={cn(
                      "block uppercase",
                      isToday ? "text-primary-600" : "text-neutral-500",
                    )}
                  >
                    {format(day, "EEE", { locale: ptBR })}
                  </Text>
                  <Text
                    variant="label"
                    className={cn(
                      "block leading-none",
                      isToday
                        ? "font-bold text-primary-700"
                        : "text-neutral-800",
                    )}
                  >
                    {format(day, "dd")}
                  </Text>
                </div>
                {hasCollects && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Badge variant="primary" className="px-1.5">
                      {total}
                    </Badge>
                    <ChevronRight
                      className="h-3.5 w-3.5 text-primary-500 opacity-0 transition-opacity group-hover:opacity-100"
                      aria-hidden
                    />
                  </div>
                )}
              </button>

              <div className="flex flex-1 flex-col gap-1.5 p-1.5">
                {collects.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center py-4">
                    <Text variant="body" size="small" className="text-neutral-300">
                      Sem coletas
                    </Text>
                  </div>
                ) : (
                  <>
                    {collects.map((collect) => (
                      <ColetaCalendarCard
                        key={collect.collect_id}
                        collect={collect}
                        onSelect={onSelect}
                      />
                    ))}
                    {overflow > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenDay?.(key)}
                        className="h-auto w-full justify-center gap-1 border-dashed border-primary-300 px-2 py-1.5 text-xs font-semibold text-primary-700 hover:border-primary-400 hover:bg-primary-50"
                      >
                        <Plus className="h-3 w-3" aria-hidden />
                        Ver mais {overflow}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
